// R115 calendar hub — the five views, as DATA (orchestrator-owned; change only through the round's
// orchestrator). A view chooses layers / stats / rail providers / opening sections / create order.
// Keys that no module has registered yet are skipped silently, so a builder that has not merged
// never breaks a view. Jesse's ask: "Views… tailored for anyone's role", without overwhelming —
// hence ≤6 stats, one active filter, one blue action, everything else behind ⋯ or the Show menu.
import { registerView, type View, type OfficeRole } from './registry';
import type { Property } from './types';

const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];

/** MTR rows: MTR-set homes plus any home with a tenant or open lead (the mtr layer exposes `homes`)
 *  — R122: plus any home with an MTR hold plan (the mtr_holds layer's `homes`; those are STR homes). */
const mtrRow = (p: Property, ctx: { layer<T>(id: string): T | null }) => {
  const m = ctx.layer<{ homes?: string[] }>('mtr');
  const h = ctx.layer<{ homes?: string[] }>('mtr_holds');
  return !!p.is_mtr || !!(m && Array.isArray(m.homes) && m.homes.includes(p.listing_id)) || !!(h && Array.isArray(h.homes) && h.homes.includes(p.listing_id));
};
/** Health rows: most open issues first, then lowest Airbnb score (health layer → { by_listing: {id: {open_issues, score}} }). */
const healthOf = (ctx: { layer<T>(id: string): T | null }, id: string) => {
  const h = ctx.layer<{ by_listing?: Record<string, { open_issues?: number; score?: number | null }> }>('health');
  return (h && h.by_listing && h.by_listing[id]) || {};
};

export const PRESETS: View[] = [
  {
    id: 'stays', label: 'Reservations', short: 'Stays', icon: '🗓', order: 10, roles: OFFICE,
    blurb: 'Who is staying where — book, hold and manage reservations.',
    layers: { stays: 'on', inquiries: 'on', blocks: 'on', rates: 'on', turns: 'dim', wos: 'dim', scores: 'on', special_offers: 'on' },
    stats: ['arriving_today', 'checkouts_today', 'in_house', 'vacant_tonight', 'negative', 'open_weekend'],
    createOrder: ['hold', 'wo', 'clean', 'inspection', 'task', 'tour'],
  },
  {
    id: 'ops', label: 'Operations', short: 'Ops', icon: '🧰', order: 20, roles: OFFICE,
    blurb: 'Cleans, inspections, work orders and tasks — what needs doing, day by day.',
    // R115 integ: wo_unscheduled 'on' (approved) — the 📥 bucket rail, the wos_due stat and the 🔧 row badges read it
    layers: { stays: 'dim', inquiries: 'off', blocks: 'dim', rates: 'off', turns: 'on', inspections: 'on', wos: 'on', wo_unscheduled: 'on', tasks: 'on', health: 'on', scores: 'off', special_offers: 'off' },
    stats: ['unassigned_cleans_7d', 'inspections_unassigned', 'wos_due', 'tasks_due_today', 'at_risk_same_day', 'open_issues'],   // R115 review: A5 — Issues
    rail: ['unassigned_cleans', 'unscheduled_wos', 'unassigned_inspections', 'tasks_no_listing', 'supply_requests'],   // + open supply requests (A5)
    createOrder: ['wo', 'clean', 'inspection', 'task', 'hold'],
  },
  {
    id: 'ops.hk', parent: 'ops', label: 'Cleans & Inspections', short: 'HK', icon: '🧹', order: 21, roles: OFFICE,
    blurb: 'Assign and move cleans and inspections; plan the week.',
    layers: { stays: 'dim', inquiries: 'off', blocks: 'dim', rates: 'off', turns: 'on', inspections: 'on', wos: 'off', tasks: 'off', health: 'off' },
    stats: ['cleans_today', 'unassigned_cleans_7d', 'inspections_unassigned', 'at_risk_same_day'],
    rail: ['unassigned_cleans', 'unassigned_inspections'],
    createOrder: ['clean', 'inspection', 'task'],
  },
  {
    id: 'ops.maint', parent: 'ops', label: 'Maintenance', short: 'Maint', icon: '🔧', order: 22, roles: OFFICE,
    blurb: 'Booked work orders on the calendar; drag or book the unscheduled ones.',
    layers: { stays: 'dim', inquiries: 'off', blocks: 'dim', rates: 'off', turns: 'dim', inspections: 'off', wos: 'on', wo_unscheduled: 'on', tasks: 'off', health: 'off' },
    stats: ['wo_open', 'wo_unscheduled', 'wo_booked_in_view', 'wo_overdue'],
    rail: ['unscheduled_wos'],
    createOrder: ['wo', 'task'],
  },
  {
    id: 'guests', label: 'Guests', short: 'Guests', icon: '💬', order: 30, roles: OFFICE,
    blurb: 'Guest sentiment, arrival checklists, recovery and messages.',
    layers: { stays: 'on', inquiries: 'off', blocks: 'dim', rates: 'off', turns: 'off', wos: 'off', guest_signals: 'on', scores: 'on', special_offers: 'on' },
    // R115 integ: the guest builder's special_offers stat lives here (Reservations keeps its 6 core stats)
    stats: ['negative_in_house', 'at_risk_arrivals', 'awaiting_review', 'recovery_open', 'special_offers'],
    rail: ['at_risk_arrivals', 'negative_in_house', 'recovery_open'],
    openSections: ['recovery', 'arrival_checklist', 'message'],
    // R115 integ fix (D2): "Hold or reserve…" is first only in Reservations — here a Task leads
    createOrder: ['task', 'hold'],
  },
  {
    id: 'mtr', label: 'MTR', short: 'MTR', icon: '🏠', order: 40, roles: OFFICE,
    blurb: 'Mid-term tenants, rent, vacancies to fill, leads and tours.',
    layers: { stays: 'on', inquiries: 'off', blocks: 'dim', rates: 'off', turns: 'off', wos: 'dim', mtr: 'on', mtr_holds: 'on' },   // R122: + the MTR hold agent's held bars
    stats: ['mtr_vacant', 'try_mtr_windows', 'rent_owed', 'tours_week', 'mtr_holds'],
    rail: ['try_mtr', 'leads_by_move_in', 'tours_week', 'mtr_holds'],
    rowFilter: (p, ctx) => mtrRow(p, ctx),
    openSections: ['mtr_tenant', 'mtr_rates'],
    createOrder: ['tour', 'lead', 'wo', 'task', 'mtr_hold'],
  },
  {
    id: 'health', label: 'Health', short: 'Health', icon: '📈', order: 50, roles: OFFICE,
    blurb: 'How each home is doing — open issues, review scores, inspection grades.',
    layers: { stays: 'dim', inquiries: 'off', blocks: 'off', rates: 'off', turns: 'off', wos: 'off', health: 'on', scores: 'on' },
    stats: ['open_issues', 'avg_score', 'reviews_30d', 'at_risk_arrivals'],
    rowSort: (a, b, ctx) => {
      const ha = healthOf(ctx, a.listing_id), hb = healthOf(ctx, b.listing_id);
      const di = (hb.open_issues || 0) - (ha.open_issues || 0);
      if (di) return di;
      return (ha.score ?? 99) - (hb.score ?? 99);
    },
    createOrder: ['task'],
  },
];

/** Everyone lands on Reservations unless they pressed "Make this my default" (localStorage stc_default_view:<uid>). */
export const DEFAULT_VIEW = 'stays';

let registered = false;
export function registerPresets(): void {
  if (registered) return;
  registered = true;
  for (const v of PRESETS) registerView(v);
}
